"use client";

import React, { useState, useEffect, useRef } from "react";
import { ButtonStyleType } from "@/types";
import { SectionWrapper } from "../components/SectionWraper";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import parse from "html-react-parser";
import Image from "@/components/ui/Image";
import { cn } from "@/lib/utils";
import Link from "next/link";

type OverlayOpacity   = "none" | "light" | "medium" | "dark" | "solid-white";
type ContentPosition  =
  | "top-left"    | "top-center"    | "top-right"
  | "middle-left" | "middle-center" | "middle-right"
  | "bottom-left" | "bottom-center" | "bottom-right";

interface Hero22Props {
  parallaxScroll?: boolean;
  contentPosition?: ContentPosition;
  overlayOpacity?: OverlayOpacity;
  minHeight?: number;
  contentInDark?: boolean;
  // Content data
  contents?: {
    badge?: string;
    title?: string;
    content?: string;
    hasButton?: boolean;
    buttonText?: string;
    buttonLink?: string;
    buttonStyle?: ButtonStyleType;
    mediaType?: "video" | "image" | "none";
    loopVideo?: string;
    image?: string;
    imageAltText?: string;
  };
  attributes?: React.HTMLProps<HTMLElement>;
}


const getVideoMimeType = (url: string): string => {
  const ext = url.split(".").pop()?.toLowerCase() ?? "";
  const map: Record<string, string> = {
    mp4: "video/mp4", webm: "video/webm",
    ogg: "video/ogg", mov: "video/quicktime",
  };
  return map[ext] ?? "video/mp4";
};

const Hero22 = ({
  parallaxScroll  = true,
  contentPosition = "bottom-center",
  overlayOpacity  = "medium",
  minHeight = 600,
  contentInDark = false,
  contents,
  attributes,
}: Hero22Props) => {
  const [videoError, setVideoError] = useState(false);
  // Initialise from the prop so the first paint already has the correct sticky class.
  // The effect below only adjusts if the viewport turns out to be shorter than minHeight.
  const [isParallaxActive, setIsParallaxActive] = useState(parallaxScroll);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const evaluate = () => {
      // Disable parallax when the viewport is shorter than minHeight —
      // the hero would be clipped by its own min-height and sticky wouldn't look right.
      const screenTooShort = !!minHeight && window.innerHeight < minHeight;
      const active = parallaxScroll && !screenTooShort;
      setIsParallaxActive(active);

      // Walk siblings forward, skipping other Hero22 wrappers (data-hero22),
      // and elevate the first regular content sibling so it slides over all sticky heroes.
      const el = wrapperRef.current;
      if (!el) return;
      let sibling = el.nextElementSibling as HTMLElement | null;
      while (sibling) {
        if (!sibling.hasAttribute("data-hero22")) {
          sibling.style.position = active ? "relative" : "";
          sibling.style.zIndex   = active ? "1" : "";
          break;
        }
        sibling = sibling.nextElementSibling as HTMLElement | null;
      }
    };

    evaluate();
    window.addEventListener("resize", evaluate);
    return () => window.removeEventListener("resize", evaluate);
  }, [parallaxScroll, minHeight]);

  const isNone  = contents?.mediaType === "none";
  const isVideo = !isNone && contents?.mediaType === "video" && !!contents?.loopVideo && !videoError;

  const overlayClass = cn(
    "absolute inset-0",
    overlayOpacity === "none"   && "bg-transparent",
    overlayOpacity === "light"  && "bg-black/20",
    overlayOpacity === "medium" && "bg-black/40",
    overlayOpacity === "dark"   && "bg-black/60",
  );

  const wrapperClass = cn(
    "absolute inset-0 flex flex-col px-6 pt-10 md:pt-16 xl:pt-20 pb-14 md:pb-30 xl:pb-40",
    // vertical
    contentPosition.startsWith("top")    && "justify-start",
    contentPosition.startsWith("middle") && "justify-center",
    contentPosition.startsWith("bottom") && "justify-end",
    // horizontal
    contentPosition.endsWith("left")   && "items-start text-left",
    contentPosition.endsWith("center") && "items-center text-center",
    contentPosition.endsWith("right")  && "items-end text-right",
  );

  const innerClass = cn(
    "w-full max-w-5xl mx-auto flex flex-col gap-4",
    contentPosition.endsWith("left")   && "items-start",
    contentPosition.endsWith("center") && "items-center",
    contentPosition.endsWith("right")  && "items-end",
  );

  return (
    <div
      ref={wrapperRef}
      data-hero22
      className={cn(isParallaxActive && "sticky z-0")}
      style={isParallaxActive ? { top: 'var(--header-h)' } : undefined}
    >
    <SectionWrapper
      spacing={'none'}
      variant={'A'}
      darkMode={false}
      heroSection={true}
      componentName="X22"
      attributes={attributes}
      removePaddingTop
      removePaddingBottom
      usedInPageNav={false}
      pageNavTitle="Hero 22"
      style={{backgroundColor: overlayOpacity === "solid-white" ? "white" : undefined}}
    >
      <div
        className="relative w-full overflow-hidden aspect-video md:aspect-auto h-[calc(100svh-var(--header-h))]"
        style={minHeight ? { minHeight: `${minHeight}px` } : undefined}
      >

        {/* Background media — skipped when mediaType is "none" */}
        {!isNone && (
          <>
            {isVideo ? (
              <video
                className="absolute inset-0 h-full w-full object-cover"
                autoPlay loop muted playsInline preload="none"
                onError={() => setVideoError(true)}
              >
                <source
                  src={contents?.loopVideo}
                  type={getVideoMimeType(contents?.loopVideo ?? "")}
                />
              </video>
            ) : (
              contents?.image && (
                <Image
                  src={contents.image}
                  alt={contents.imageAltText ?? ""}
                  title={contents.imageAltText ?? ""}
                  fill
                  className="object-cover"
                  priority
                  fetchPriority="high"
                />
              )
            )}

            {/* Fallback image when video errors */}
            {contents?.mediaType === "video" && videoError && contents?.image && (
              <Image
                src={contents.image}
                alt={contents.imageAltText ?? ""}
                title={contents.imageAltText ?? ""}
                fill
                className="object-cover"
                priority
                fetchPriority="high"
              />
            )}

            {/* Overlay */}
            <div className={overlayClass} />
          </>
        )}

        {/* Content */}
        <div className={wrapperClass}>
          <div className={innerClass} data-aos="fade-in" data-aos-duration="1000">
            {contents?.badge && (
              <Badge variant="outline" bold className={ contents.mediaType === "none" ? "text-foreground/70 border-foreground/70" : contentInDark ? "in-dark-content" : "text-white border-white/80"}>
                {contents.badge}
              </Badge>
            )}
            {contents?.title && (
              <h1 className={ contents.mediaType === "none" ? "" : contentInDark ? "in-dark-content" : "text-white"}>{parse(contents.title)}</h1>
            )}
            {contents?.content && (
              <div className={cn("paragraph-lg max-w-[58ch]", contents.mediaType === "none" ? "text-foreground/70" : contentInDark ? "in-dark-content text-foreground/70" : "text-white/80")}>
                {parse(contents.content)}
              </div>
            )}
            {contents?.hasButton && (
              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                <div className="flex flex-col">
                  <Button
                    size="lg"
                    variant={contents.buttonStyle ?? "primary"}
                    className={cn("w-full sm:w-auto", contents.mediaType === "none" ? "" : contentInDark ? "in-dark-content" : "")} 
                    asChild
                  >
                    <Link href={contents.buttonLink ?? ""}>
                      {contents.buttonText}
                    </Link>
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </SectionWrapper>
    </div>
  );
};

export { Hero22 };
